import React, { useState, useEffect, useRef } from 'react';
import { ToggleButtons } from './UI';
import './Health.css';

function RowButton({setValue, name, currentValue, value, label}) {
	return (
		<button
			onClick={() => setValue(name, {[name]: value})}
			className={currentValue === value ? `active ${value}` : ''}
		>
			{label}
		</button>
	);
}

function Health({state, setDataValue}) {
	const [foodInput, setFoodInput] = useState('');
	const [descriptionInput, setDescriptionInput] = useState('');
	const [notesInput, setNotesInput] = useState('');
	const [stage, setStage] = useState('search'); // 'search' or 'details' or 'edit_log'
	const [editLogEntry, setEditLogEntry] = useState(null);
	const [editTime, setEditTime] = useState('');
	const [editValue, setEditValue] = useState('');
	const [editMeal, setEditMeal] = useState('');
	const descriptionRef = useRef(null);
	const notesRef = useRef(null);

	const meals = (state?.data?.meals || []).map(meal => {
		const newMeal = [...meal];
		if (newMeal[1]) {
			newMeal[1] = newMeal[1].replace(/\\n/g, '\n');
		}
		return newMeal;
	});

	const handleAddFood = () => {
		const trimmedFood = foodInput.trim();
		if (trimmedFood) {
			const existingMeal = meals.find(f => f[0].toLowerCase() === trimmedFood.toLowerCase());
			if (existingMeal) {
				setDescriptionInput(existingMeal[1] || '');
			} else {
				setDescriptionInput('');
			}
			setNotesInput('');
			setStage('details');
		}
	};

	const handleSubmitFood = () => {
		const trimmedFood = foodInput.trim();
		if (trimmedFood) {
			setDataValue('food', {meal: trimmedFood, description: descriptionInput.trim(), notes: notesInput.trim()});
			setFoodInput('');
			setDescriptionInput('');
			setNotesInput('');
			setStage('search');
		}
	};

	const handleSuggestionClick = (food) => {
		setFoodInput(food[0]);
		setDescriptionInput(food[1] || '');
		setNotesInput('');
		setStage('details');
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Enter') {
			if (stage === 'search') {
				handleAddFood();
			} else {
				handleSubmitFood();
			}
		}
	};

	const handleBack = () => {
		setDescriptionInput('');
		setNotesInput('');
		setStage('search');
	};

	const handleClearDescription = () => {
		setDescriptionInput('');
		descriptionRef.current?.focus({preventScroll: true});
	};

	const handleClearNotes = () => {
		setNotesInput('');
		notesRef.current?.focus({preventScroll: true});
	};

	const handleLogClick = (entry) => {
		setEditLogEntry(entry);
		
		// Format time for datetime-local input
		const d = new Date(entry.time);
		const tzOffset = d.getTimezoneOffset() * 60000;
		const localISOTime = (new Date(d - tzOffset)).toISOString().slice(0, 16);
		setEditTime(localISOTime);
		
		if (entry.measurement === 'food') {
			setEditMeal(entry.meal || '');
			setDescriptionInput(entry.description || '');
			setNotesInput(entry.notes || '');
		} else {
			setEditValue(entry[entry.measurement] || '');
		}
		setStage('edit_log');
	};

	const handleSaveLog = () => {
		const newTime = new Date(editTime).toISOString();
		let fields = {};
		if (editLogEntry.measurement === 'food') {
			fields = {
				meal: editMeal.trim(),
				description: descriptionInput.trim(),
				notes: notesInput.trim()
			};
		} else {
			fields = { [editLogEntry.measurement]: editValue };
		}
		
		setDataValue('edit_life_log', {
			measurement: editLogEntry.measurement,
			old_time: editLogEntry.time,
			new_time: newTime,
			fields: fields
		});
		
		setStage('search');
		setFoodInput('');
		setDescriptionInput('');
		setNotesInput('');
		setEditLogEntry(null);
	};

	const handleDeleteLog = () => {
		if (window.confirm('Are you sure you want to delete this entry?')) {
			setDataValue('delete_life_log', {
				measurement: editLogEntry.measurement,
				time: editLogEntry.time
			});
			setStage('search');
			setFoodInput('');
			setDescriptionInput('');
			setNotesInput('');
			setEditLogEntry(null);
		}
	};

	const filteredFood = meals.filter(f =>
		f[0].toLowerCase().includes(foodInput.trim().toLowerCase())
	);


	const lifeLog = state?.data?.lifeLog || [];
	console.log(lifeLog);
	const groupedLog = {};
	lifeLog.forEach(entry => {
		const date = new Date(entry.time);
		const day = date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
		if (!groupedLog[day]) groupedLog[day] = [];
		groupedLog[day].push(entry);
	});

	useEffect(() => {
		if (stage === 'details') {
			if (descriptionInput === '') {
				descriptionRef.current?.focus({preventScroll: true});
			} else {
				notesRef.current?.focus({preventScroll: true});
			}
		}
	}, [stage]);

	return (
		<div className='health'>
			<div className='row'>
				S:
				<div className='arm'>
					<RowButton setValue={setDataValue} name='sleep' currentValue={state?.data?.sleep} value='good' label='Good' />
					<RowButton setValue={setDataValue} name='sleep' currentValue={state?.data?.sleep} value='average' label='Average' />
					<RowButton setValue={setDataValue} name='sleep' currentValue={state?.data?.sleep} value='bad' label='Bad' />
				</div>
			</div>

			<div className='row'>
				T:
				<div className='arm'>
					<RowButton setValue={setDataValue} name='bm' currentValue={state?.data?.bm} value='good' label='Good' />
					<RowButton setValue={setDataValue} name='bm' currentValue={state?.data?.bm} value='borderline' label='Borderline' />
					<RowButton setValue={setDataValue} name='bm' currentValue={state?.data?.bm} value='bad' label='Bad' />
				</div>
			</div>

			{stage !== 'edit_log' && (
				<div className={`food-search ${stage === 'search' && !foodInput ? 'full-width' : ''}`}>
					<input
						type='text'
						value={foodInput}
						onChange={(e) => setFoodInput(e.target.value)}
						onKeyDown={handleKeyDown}
						placeholder='What did you eat?'
					/>
					{(stage === 'details' || (stage === 'search' && foodInput)) &&
						<button onClick={stage === 'search' ? () => setFoodInput('') : handleSubmitFood}>
							{stage === 'search' ? 'Clear' : 'Submit'}
						</button>
					}
				</div>
			)}

			{stage === 'search' && filteredFood.length > 0 &&
				<div className='food-suggestions'>
					{filteredFood.map((food, index) =>
						<div
							key={index}
							className='suggestion-item'
							onClick={() => handleSuggestionClick(food)}
						>
							<div>{food[0]}</div>
							{food[1] && <div className='suggestion-description'>{food[1]}</div>}
						</div>
					)}
				</div>
			}

			{stage === 'edit_log' && editLogEntry &&
				<div className='food-details'>
					<input 
						type="datetime-local" 
						value={editTime} 
						onChange={(e) => setEditTime(e.target.value)} 
						className="time-edit"
					/>
					{editLogEntry.measurement === 'food' && (
						<>
							<input
								type='text'
								value={editMeal}
								onChange={(e) => setEditMeal(e.target.value)}
								placeholder='Meal'
								className="meal-edit"
							/>
							<textarea
								value={descriptionInput}
								onChange={(e) => setDescriptionInput(e.target.value)}
								placeholder='Food Description'
							/>
							<textarea
								value={notesInput}
								onChange={(e) => setNotesInput(e.target.value)}
								placeholder='Specific Notes'
							/>
						</>
					)}
					{editLogEntry.measurement !== 'food' && (
						<select
							value={editValue}
							onChange={(e) => setEditValue(e.target.value)}
							className="value-edit"
						>
							{editLogEntry.measurement === 'sleep' && (
								<>
									<option value="good">Good</option>
									<option value="average">Average</option>
									<option value="bad">Bad</option>
								</>
							)}
							{editLogEntry.measurement === 'bm' && (
								<>
									<option value="good">Good</option>
									<option value="borderline">Borderline</option>
									<option value="bad">Bad</option>
								</>
							)}
						</select>
					)}
					<div className='details-buttons'>
						<button onClick={handleSaveLog}>Save</button>
						<button onClick={handleDeleteLog} style={{background: '#833'}}>Delete</button>
						<button onClick={handleBack}>Cancel</button>
					</div>
				</div>
			}

			{stage === 'search' && Object.keys(groupedLog).length > 0 &&
				<div className='life-log'>
					{Object.entries(groupedLog).map(([day, entries]) => (
						<div key={day} className='log-day-group'>
							<div className='log-day-header'>{day}</div>
							{entries.map((entry, idx) => {
								const timeStr = new Date(entry.time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
								const isFood = entry.measurement === 'food';
								const isSleep = entry.measurement === 'sleep';
								const valueClass = !isFood ? entry[entry.measurement] : '';
								return (
									<div key={idx} className={`log-entry ${isFood ? 'left' : `right ${isSleep ? 'sleep' : ''} ${valueClass}`}`} onClick={() => handleLogClick(entry)}>
										<div className='log-time'>{timeStr}</div>
										<div className='log-content'>
											{isFood ? entry.meal : `${entry.measurement}: ${entry[entry.measurement]}`}
										</div>
									</div>
								);
							})}
						</div>
					))}
				</div>
			}

			{stage === 'details' &&
				<div className='food-details'>
					<textarea
						ref={descriptionRef}
						value={descriptionInput}
						onChange={(e) => setDescriptionInput(e.target.value)}
						placeholder='Food Description'
					/>
					<textarea
						ref={notesRef}
						value={notesInput}
						onChange={(e) => setNotesInput(e.target.value)}
						placeholder='Specific Notes'
					/>
					<div className='details-buttons'>
						<button onClick={handleClearDescription}>Clear Description</button>
						<button onClick={handleClearNotes}>Clear Notes</button>
						<button onClick={handleBack}>Back</button>
					</div>
				</div>
			}

		</div>
	);
}

export default Health;
